import {
    ColumnDef,
    Header,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { ComponentProps, RefObject, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from '../../../../components/common/Table/Table'
import { TableLoadingOverlay } from '../../../../components/common/Table/TableLoadingOverlay'
import { InvoiceBill } from 'src/hooks'
import { useInvoiceBillsLabelsMap } from './useInvoiceBillsLabelsMap'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ErrorState, SortingOrder } from '../../../../components'
import { useBillingApi } from '../../../../hooks/useApi/useBillingApi/useBillingApi'
import { useColumns } from './useColumns'
import { usePaginationConfig } from '../../../../hooks/usePaginationConfig/usePaginationConfig'
import { ContentPlaceholder } from '@/components/molecules'

type InvoiceBillsTableContainerProps = {
    invoiceId: string
}

export const InvoiceBillsTableContainer: React.FC<
    InvoiceBillsTableContainerProps
> = (props) => {
    const { invoiceId } = props
    const labelsMap = useInvoiceBillsLabelsMap()

    const navigate = useNavigate()

    const { sortBy, order, page, recordsPerPage } = useSearch({
        from: '/_layout/invoices/$invoiceId',
    })

    const onTableColumnHeaderClick = useCallback(
        (header: Header<unknown, unknown>) => {
            const columnId = header.id
            const newOrder =
                sortBy !== columnId || order === 'asc' ? 'desc' : 'asc'
            const newSortBy = columnId

            void navigate({
                to: '/invoices/$invoiceId',
                from: '/invoices/$invoiceId',
                search: (prev) => {
                    const next = {
                        ...prev,
                        sortBy: newSortBy,
                        order: newOrder,
                    }

                    return next
                },
            })
        },
        [navigate, order, sortBy]
    )

    const getColumnSorting = useCallback(
        (header: Header<unknown, unknown>) => {
            if (sortBy === header.id) {
                return order as SortingOrder
            }
            return undefined
        },
        [order, sortBy]
    )

    const onPaginationInfoChange = useCallback(
        ({
            page,
            recordsPerPage,
        }: {
            page: number
            recordsPerPage: number
        }) => {
            void navigate({
                to: `/invoices/$invoiceId`,
                from: `/invoices/$invoiceId`,
                search: (prev) => {
                    const next = {
                        ...prev,
                        page: page,
                        recordsPerPage: recordsPerPage,
                    }

                    return next
                },
            })
        },
        [navigate]
    )

    const { useGetInvoiceBills } = useBillingApi()
    const { data, isSuccess, isLoading, isFetched, isFetching, isError } =
        useGetInvoiceBills(invoiceId, {
            sortBy,
            order,
            offset: (page - 1) * recordsPerPage,
            limit: recordsPerPage,
        })

    const columns = useColumns(invoiceId, labelsMap, data?.items)

    const pagination = usePaginationConfig({
        totalItems: data?.totalItems || 0,
        onPageChange: (newPage) => {
            onPaginationInfoChange({
                page: newPage,
                recordsPerPage: Number(pagination.recordsPerPage),
            })
        },
        onRecordsPerPageChange: (newRecordsPerPage) => {
            onPaginationInfoChange({
                page: 1,
                recordsPerPage: Number(newRecordsPerPage),
            })
        },
        siblingCount: 2,
        currentPage: page,
        recordsPerPage,
    })
    const emptyData = Array.from(new Array(50)).map(() => {
        const emptyInvoiceBillsList = {}

        return emptyInvoiceBillsList
    })

    if (isSuccess && data.items.length === 0) {
        return <InvoiceBillsEmptyState />
    }

    if (isError) {
        return <InvoiceBillsErrorState />
    }

    return (
        <InvoiceBillsTable
            invoiceId={invoiceId}
            columns={columns}
            onTableColumnHeaderClick={onTableColumnHeaderClick}
            getColumnSorting={getColumnSorting}
            data={data?.items ?? emptyData}
            isLoading={isFetching || isLoading || (!isFetched && !isFetching)}
            pagination={pagination}
        />
    )
}

const InvoiceBillsEmptyState = () => {
    const { t } = useTranslation('translations')
    const title = t('invoice_table.empty_state.title')
    const description = t('invoice_table.empty_state.description')

    return (
        <ContentPlaceholder
            className="h-full"
            illustrationSrc="/assets/empty-state-illustration.svg"
            title={title}
            description={description}
            name="empty-state"
        />
    )
}

const InvoiceBillsErrorState = () => {
    const { t } = useTranslation('translations')

    return (
        <ErrorState
            description={t('invoice_table.error_state.description')}
            className="h-full"
        />
    )
}
export type InvoiceBillsTableProps = {
    invoiceId: string
    data: InvoiceBill[] | Record<never, unknown>[]
    columns: ColumnDef<InvoiceBill>[]
    onTableColumnHeaderClick: ComponentProps<
        typeof Table
    >['onTableColumnHeaderClick']
    getColumnSorting: ComponentProps<typeof Table>['getColumnSorting']
    isLoading: boolean
    pagination: ComponentProps<typeof Table>['pagination']
    containerRef?: RefObject<HTMLDivElement>
    tableBodyRef?: RefObject<HTMLTableSectionElement>
    tableWrapperRef?: RefObject<HTMLDivElement>
    tableRef?: RefObject<HTMLTableElement>
}

export const InvoiceBillsTable: React.FC<InvoiceBillsTableProps> = (props) => {
    const {
        data,
        columns,
        onTableColumnHeaderClick,
        getColumnSorting,
        isLoading,
        pagination,
        containerRef,
        tableBodyRef,
        tableWrapperRef,
        tableRef,
    } = props

    const table = useReactTable({
        data: data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
    })

    const { t } = useTranslation('translations')
    const loadingReportTitle = t('common.loading_report')

    return (
        <div
            className="relative h-full"
            ref={containerRef}
            data-testid="invoice-bills-table"
        >
            <Table
                table={table}
                onTableColumnHeaderClick={onTableColumnHeaderClick}
                getColumnSorting={getColumnSorting}
                pagination={pagination}
                style="list"
                tableWrapperRef={tableWrapperRef}
                tableBodyRef={tableBodyRef}
                tableRef={tableRef}
            />

            <TableLoadingOverlay show={isLoading} title={loadingReportTitle} />
        </div>
    )
}
